import React, { useState, useRef, useCallback } from 'react'
import { Dispatch } from 'redux'
import { CloudUploadOutlined } from '@material-ui/icons'
import { useDropzone } from 'react-dropzone'
import { IFile, OnProgressCallbackType } from '../../types/file'
import { IEditionMetadata } from '../../types/pack'
import {
  closeUploadDialog,
  readLocalFile,
  readLocalFileCancel,
  uploadFile,
  setProgress,
} from '../../actions/file'
import { getPackById } from '../../actions/pack'
import { triggerNotification } from '../../actions/notifications'
import { getMatchedEdition } from '../../services/pack'
import { readUploadedFile, replacePastedImageName } from '../../services/file'
import { IColoredTheme } from '../../styles'
import { Space } from '../../layouts'
import UploadModal from '../UploadModal'
import { P } from '../StyledText'
import { PastedImageField } from '../PastedImageField'
import { StyledFileZone } from './styled'

export interface IFileUploadZone extends IColoredTheme {
  id: string
  file: IFile
  packId?: string
  groupId: string
  editions: IEditionMetadata[]
  currentEdition?: IEditionMetadata
  disabled?: boolean
  allowPastingImages: boolean
  dispatch: Dispatch
}

export const FileUploadZoneComponent = ({
  id,
  file,
  packId,
  groupId,
  editions,
  currentEdition,
  disabled,
  theme,
  allowPastingImages,
  dispatch,
}: IFileUploadZone) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileTemporaryURL, setFileTemporaryURL]: [
    string | null,
    any
  ] = useState(null)
  const withProgress: OnProgressCallbackType = (progress: number) => {
    dispatch(setProgress(progress))
  }

  const handleUploadFile = (name: string, editionsToUpdate: string[]) => {
    dispatch(uploadFile(withProgress)(id, name, groupId, editionsToUpdate))
  }

  const handleClose = (): void => {
    // Fixes the issue when you upload file and cancel modal
    // and then try to upload the same file - modal do not shows
    if (inputRef && inputRef.current && inputRef.current.value) {
      inputRef.current.value = ''
    }
    setFileTemporaryURL(null)
    dispatch(closeUploadDialog())
    if (packId) {
      dispatch(getPackById(packId))
    }
  }

  const handlePasteImageError = (message: string): void => {
    dispatch(triggerNotification(message))
  }

  const handleFileUpload = useCallback(
    async ([fileInstance, ...otherFiles]: File[]) => {
      if (otherFiles.length !== 0) {
        dispatch(readLocalFileCancel('You can only upload one file at a time.'))
        return
      }
      const existingEdition =
        currentEdition ||
        getMatchedEdition(
          editions,
          fileInstance && replacePastedImageName(fileInstance.name)
        )
      dispatch(readLocalFile(id, fileInstance, groupId, existingEdition))
      const fileURL = await readUploadedFile(fileInstance, dispatch)
      setFileTemporaryURL(fileURL)
    },
    [dispatch, currentEdition, editions, id, setFileTemporaryURL, groupId]
  )
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileUpload,
    disabled,
  })
  return (
    <>
      <StyledFileZone {...getRootProps()} disabled={disabled} theme={theme}>
        <section>
          <P>Drag and drop files here or click to browse</P>
          <CloudUploadOutlined />
        </section>
        <input
          {...getInputProps()}
          ref={inputRef}
          id={id}
          hidden
          disabled={disabled}
        />
      </StyledFileZone>
      {allowPastingImages && (
        <Space mt={2}>
          <PastedImageField
            disabled={disabled}
            onFileUpload={handleFileUpload}
            onError={handlePasteImageError}
          />
        </Space>
      )}
      <UploadModal
        open={file.openedModal === id}
        title="Upload file"
        actionLabel="Upload"
        action={handleUploadFile}
        handleClose={handleClose}
        file={file}
        showThumbnail={file.mimetype.startsWith('image/')}
        fileTemporaryURL={fileTemporaryURL}
        onDrop={() => {
          dispatch(readLocalFileCancel('Only one file could be dropped.'))
        }}
      />
    </>
  )
}
